import { ready, onceInitialized } from '../../lib/lifecycle/ready';

function initVerticalMenu(root) {
  const scope = root || document;
  // Only target stacked variation blocks
  const stackedMenus = scope.querySelectorAll('.wp-block-modulr-vertical-menu[data-variation="stacked"]');

  // Helper function to deactivate a menu item and all its nested children
  const deactivateMenuItem = (menuItem) => {
    if (!menuItem) return;

    menuItem.classList.remove('sub-active');
    menuItem.setAttribute('aria-expanded', 'false');

    const submenu = menuItem.querySelector(':scope > .children, :scope > .sub-menu, :scope > ul');
    if (submenu) {
      submenu.style.setProperty('left', '110%', 'important');
      submenu.style.top = '';
      submenu.style.width = '';
      submenu.style.height = '';
      submenu.style.display = '';
      submenu.style.flexDirection = '';
      submenu.style.zIndex = '100';
    }

    const directSubmenu = menuItem.querySelector(':scope > .children, :scope > .sub-menu, :scope > ul');
    if (directSubmenu) {
      const nestedItems = directSubmenu.querySelectorAll(':scope > li.menu-item-has-children.sub-active, :scope > li.page_item_has_children.sub-active');
      nestedItems.forEach((nestedItem) => {
        deactivateMenuItem(nestedItem);
      });
    }
  };

  const getVisibleLayer = (nav) => {
    const activeParent = nav.querySelector('li.sub-active');
    if (activeParent) {
      return activeParent.querySelector(':scope > .children, :scope > .sub-menu, :scope > ul');
    }
    return nav.querySelector(':scope > ul');
  };

  const updateStackedMenuHeight = (nav) => {
    const visibleLayer = getVisibleLayer(nav);
    const rootList = nav.querySelector(':scope > ul');
    if (!visibleLayer) return;
    const contentHeight = visibleLayer.scrollHeight;
    nav.style.height = contentHeight + 'px';
    if (visibleLayer !== rootList) {
      visibleLayer.style.height = contentHeight + 'px';
    }
  };

  const getSubmenuDepth = (submenu, nav) => {
    const rootUl = nav.querySelector(':scope > ul');
    if (!submenu || !rootUl || !rootUl.contains(submenu)) {
      return 1;
    }
    let depth = 0;
    let el = submenu;
    while (el && el !== rootUl) {
      if (el.tagName === 'UL' || el.tagName === 'OL') {
        depth += 1;
      }
      el = el.parentElement;
    }
    return Math.min(Math.max(depth, 1), 3);
  };

  const getBackTextForDepth = (menuBlock, depth) => {
    const d = Math.min(Math.max(depth, 1), 3);
    const override = menuBlock.dataset[`l${d}BackText`];
    if (override !== undefined && override !== '') {
      return override;
    }
    return menuBlock.dataset.stackedBackButtonText || 'Back';
  };

  const getBackIconForDepth = (menuBlock, depth) => {
    const d = Math.min(Math.max(depth, 1), 3);
    const override = menuBlock.dataset[`l${d}BackIcon`];
    if (override === 'false') {
      return false;
    }
    if (override === 'true') {
      return true;
    }
    return menuBlock.dataset.stackedBackButtonIcon !== 'false';
  };

  const observers = [];

  stackedMenus.forEach((menuBlock) => {
    if (!onceInitialized(menuBlock, 'vertical-menu')) return;

    // Inject back buttons before each submenu
    const nav = menuBlock.querySelector('nav');
    const submenus = menuBlock.querySelectorAll('.menu-item-has-children > .sub-menu, .page_item_has_children > .children, .page_item_has_children > ul');

    submenus.forEach((submenu) => {
      if (submenu.querySelector('.menu-back-button-wrapper')) {
        return;
      }

      const depth = nav ? getSubmenuDepth(submenu, nav) : 1;
      const backButtonText = getBackTextForDepth(menuBlock, depth);
      const showIcon = getBackIconForDepth(menuBlock, depth);

      const parentLi = submenu.parentElement;
      const parentLink = parentLi?.querySelector(':scope > a') || parentLi?.querySelector('a');
      const parentHref = parentLink?.getAttribute('href');
      const isRealUrl = parentHref && parentHref !== '#' && parentHref !== '#/';

      if (isRealUrl) {
        const parentLinkItem = document.createElement('li');
        parentLinkItem.className = 'menu-item menu-item-parent-link';
        parentLinkItem.innerHTML = parentLink.outerHTML;
        submenu.insertBefore(parentLinkItem, submenu.firstChild);
      }

      const backButtonLi = document.createElement('li');
      backButtonLi.className = 'menu-back-button-wrapper';
      backButtonLi.setAttribute('data-submenu-depth', String(depth));
      const backBtn = document.createElement('div');
      backBtn.className = 'menu-back-button';
      backBtn.setAttribute('role', 'button');
      backBtn.setAttribute('tabindex', '0');
      backBtn.setAttribute('aria-label', backButtonText);
      const backTextSpan = document.createElement('span');
      backTextSpan.className = 'back-text';
      backTextSpan.textContent = backButtonText;
      backBtn.appendChild(backTextSpan);
      if (showIcon) {
        const icon = document.createElement('span');
        icon.className = 'back-icon';
        backBtn.appendChild(icon);
      }
      backButtonLi.appendChild(backBtn);
      submenu.insertBefore(backButtonLi, submenu.firstChild);
    });

    const setArrowAlignment = () => {
      const allParentItems = menuBlock.querySelectorAll('li.menu-item-has-children, li.page_item_has_children');
      allParentItems.forEach((li) => {
        const computedStyle = window.getComputedStyle(li);
        const arrowAlignment = computedStyle.getPropertyValue('--stacked-arrow-alignment')?.trim();
        if (arrowAlignment === 'left') {
          li.setAttribute('data-arrow-alignment', 'left');
        } else {
          li.removeAttribute('data-arrow-alignment');
        }
      });
    };

    if (nav) {
      updateStackedMenuHeight(nav);
    }

    setArrowAlignment();

    const observer = new MutationObserver(() => {
      setArrowAlignment();
    });
    observer.observe(menuBlock, { attributes: true, attributeFilter: ['style'] });
    observers.push(observer);

    const parentItems = menuBlock.querySelectorAll('li.menu-item-has-children, li.page_item_has_children');

    parentItems.forEach((parentLi) => {
      const link = parentLi.querySelector(':scope > a') || parentLi.querySelector('a');
      if (!link) return;

      link.style.pointerEvents = 'none';

      link.addEventListener('click', (e) => {
        if (parentLi.classList.contains('menu-item-has-children') || parentLi.classList.contains('page_item_has_children')) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      }, true);

      parentLi.addEventListener('click', (e) => {
        if (e.target.closest('.menu-back-button') || e.target.closest('.menu-item-parent-link')) {
          return;
        }

        const submenuElement = parentLi.querySelector('.sub-menu, .children, ul');
        if (submenuElement && submenuElement.contains(e.target)) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const siblings = Array.from(parentLi.parentElement.children);
        siblings.forEach((sibling) => {
          if (sibling !== parentLi && (sibling.classList.contains('menu-item-has-children') || sibling.classList.contains('page_item_has_children'))) {
            deactivateMenuItem(sibling);
          }
        });

        parentLi.classList.add('sub-active');
        parentLi.setAttribute('aria-expanded', 'true');

        const submenu = parentLi.querySelector('.children, .sub-menu, ul');
        if (submenu) {
          const parentNav = submenu.closest('nav');
          submenu.style.setProperty('left', '0', 'important');
          submenu.style.top = '0';
          submenu.style.zIndex = '101';
          submenu.style.width = '100vw';
          submenu.style.display = 'flex';
          submenu.style.flexDirection = 'column';
          if (parentNav) {
            updateStackedMenuHeight(parentNav);
          }
        }
      });

      link.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();

          const siblings = Array.from(parentLi.parentElement.children);
          siblings.forEach((sibling) => {
            if (sibling !== parentLi && (sibling.classList.contains('menu-item-has-children') || sibling.classList.contains('page_item_has_children'))) {
              deactivateMenuItem(sibling);
            }
          });

          parentLi.classList.add('sub-active');
          parentLi.setAttribute('aria-expanded', 'true');

          const submenu = parentLi.querySelector('.children, .sub-menu, ul');
          const parentNav = submenu?.closest('nav');
          if (submenu && parentNav) {
            submenu.style.setProperty('left', '0', 'important');
            submenu.style.top = '0';
            submenu.style.zIndex = '101';
            submenu.style.width = '100vw';
            submenu.style.display = 'flex';
            submenu.style.flexDirection = 'column';
            updateStackedMenuHeight(parentNav);
          }
        }
      });
    });

    menuBlock.addEventListener('click', (e) => {
      const backBtn = e.target.closest('.menu-back-button');
      if (backBtn) {
        e.stopPropagation();
        e.preventDefault();

        const backButtonWrapper = backBtn.closest('.menu-back-button-wrapper');
        if (backButtonWrapper) {
          const submenu = backButtonWrapper.parentElement;
          const parentLi = submenu?.parentElement;
          if (parentLi && (parentLi.classList.contains('menu-item-has-children') || parentLi.classList.contains('page_item_has_children'))) {
            deactivateMenuItem(parentLi);
            const navEl = menuBlock.querySelector('nav');
            if (navEl) updateStackedMenuHeight(navEl);
          }
        }
      }
    });

    menuBlock.addEventListener('keydown', (e) => {
      const backBtn = e.target.closest('.menu-back-button');
      if (backBtn && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();

        const backButtonWrapper = backBtn.closest('.menu-back-button-wrapper');
        if (backButtonWrapper) {
          const submenu = backButtonWrapper.parentElement;
          const parentLi = submenu?.parentElement;
          if (parentLi && (parentLi.classList.contains('menu-item-has-children') || parentLi.classList.contains('page_item_has_children'))) {
            deactivateMenuItem(parentLi);
            const navEl = menuBlock.querySelector('nav');
            if (navEl) updateStackedMenuHeight(navEl);
          }
        }
      }
    });
  });

  return function destroy() {
    observers.forEach((observer) => observer.disconnect());
  };
}

ready(initVerticalMenu, { scope: 'modulr-vertical-menu' });
